import modalStyle from '../org-management-modal.module.scss';
import { Row, Col } from 'react-bootstrap';

// Components
import Input from 'components/Input';
import Button from 'components/Button';
import ModalContainer from 'components/ModalContainer';
import DropdownWithItem from 'components/DropdownWithItem';
import DragAndDropImgContianer from 'components/DragAndDropImgContianer';
import MessageBoxGroup from 'components/MessageBoxGroup';
import SpinnerForModal from "ui/SpinnerForModal";

import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { cloneDeep, isNil, size, orderBy, startsWith, has } from 'lodash';
import validator from "validator";

import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

import { organizationType } from 'const/nuclias/organization';
import { userType } from "const/nuclias/user";
import { defaultMessages } from "../const";

import orgService from "services/Auth.Organization.service";
import modalController from './modalController';

const iconActions = {
  new: 'new',
  edit: 'edit',
  del: 'delete',
  def: 'default'
}

const defaultInfo = {
  name: {
    value: '',
    isInvalid: false
  },
  iconOrigin: null, // for string
  icon: null, // for string & check update
  owner: '',
  phone: '',
  email: '',
  serviceProviderCountry: {},
  file: null, //for file
};

const OrgManagementOrgModal = ({
  info,
  modalStatus,
  handleModals,
  submitModal,
}) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showModalSpinner = useSelector(selectShowModalSpinner);
  const { supportSetting: { supportedRegions }, pageContext } = useSelector((store) => store);

  const modalInfo = {
    isCreate: modalStatus.org.type === 'create' ? true : false,
    isMSP: info.userType === userType.sp ? true : false,
  }
  const typeOptions = modalInfo.isMSP ? [{ title: 'MSP', value: 0, isActive: true }] : [{ title: 'Organization', value: 1, isActive: true }]

  const [multiMessages, setMultiMessages] = useState(cloneDeep(defaultMessages));
  const [modalData, setModalData] = useState(cloneDeep(defaultInfo));
  const [modalTitle, setModalTitle] = useState(cloneDeep(defaultInfo));
  const [options, setOptions] = useState({ country: [] });
  const dpRef = useRef()

  const init = async () => {
    try {
      dispatch(uiActions.showModalSpinner());
      setModalTitle(modalController.orgModalTitle(modalStatus, modalInfo))
      modalInfo.isCreate ? genOptions() : await setEditData()
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(uiActions.hideModalSpinner());
    }
  };

  const setEditData = async () => {
    const editObj = modalStatus.org.editObj
    let target = {}
    if (info.userType === userType.si) {
      target = editObj.orgType === organizationType.si ? pageContext.si : await modalController.getOrg(editObj)
    } else if (info.userType === userType.org) {
      target = pageContext.selectedOrg
    } else {
      target = editObj.orgType === organizationType.sp ? pageContext.sp : modalController.findMSP(editObj)
    }
    const tmpData = cloneDeep(defaultInfo)
    tmpData.id = target.id
    for (const [key, value] of Object.entries(tmpData)) {
      if (has(target, key)) {
        if (has(value, 'value'))
          tmpData[key].value = target[key]
        else
          tmpData[key] = target[key]
      }
    }

    if (!isNil(target.iconS3Path)) {
      tmpData.iconOrigin = cloneDeep(target.iconS3Path)
      tmpData.icon = target.iconS3Path
    }
    setModalData(tmpData)
  }

  const genOptions = () => {
    let countries = []
    for (const reg of supportedRegions) {
      for (const country of reg.service_countries) {
        const tmp = cloneDeep(country)
        tmp.title = tmp.country_name
        countries.push(tmp)
      }
    }
    countries = orderBy(countries, [item => item.title.toLowerCase()], 'asc');
    countries[0].isActive = true
    setOptions({ country: countries })
    setModalData({ ...modalData, serviceProviderCountry: countries[0] })
  }

  const changeRequired = (key, value) => {
    const copy = cloneDeep(modalData)
    copy[key].value = value
    copy[key].isInvalid = checkInvalid(key, value)
    setModalData(copy)
  }

  const checkInvalid = (key, value = '') => {
    let res = true;
    const target = modalData[key]
    switch (key) {
      case 'name':
        res = validator.isLength(value, { min: 1, max: 64 })
        break;
      case 'owner':
        if (size(target) > 0) res = validator.isLength(target, { min: 1, max: 64 })
        break;
      case 'phone':
        if (size(target) > 0) res = validator.isLength(target, { min: 1, max: 32 })
        break;
      case 'email':
        if (size(target) > 0)
          res = validator.isEmail(target) && validator.isLength(target, { min: 1, max: 128 })
        break;
      default:
        res = true
    }
    return !res
  }

  const checkFormInvalid = () => {
    const checkList = [
      { key: 'name', value: true },
      { key: 'owner' },
      { key: 'phone' },
      { key: 'email' }
    ]

    let res = false
    for (const item of checkList) {
      const value = item.key ? modalData[item.key]?.value : null
      if (checkInvalid(item.key, value)) {
        res = true;
        break;
      }
    }
    return res
  }

  const changeCountry = (e) => {
    const copy = cloneDeep(options)
    for (let item of copy.country) {
      item.id === e.id ? item.isActive = true : delete item.isActive
    }
    setOptions(copy)
    setModalData({ ...modalData, serviceProviderCountry: e })
  }

  const toTargetCountry = (e) => {
    const key = e.key.toUpperCase()
    let targetIndex = 0;
    for (const [index, option] of options.country.entries()) {
      if (startsWith(option.title, key)) {
        changeCountry(option)
        targetIndex = index
        break
      }
    }
    const offsetTop = dpRef.current.children[targetIndex].offsetTop
    dpRef.current.scrollTo({ top: offsetTop });
  }

  const save = async () => {
    try {
      dispatch(uiActions.showModalSpinner());
      const params = { iconActions, modalData, typeOptions, modalInfo }
      const { obj, iconObj } = modalController.formatData(params)
      let res = null
      if (modalInfo.isCreate) {
        res = await orgService.createOrg(obj)
      } else {
        res = await orgService.updateOrg(obj.id, obj)
        if (iconObj.action === iconActions.del) {
          await orgService.deleteOrgIcon(obj.id)
        } else if (iconObj.action === iconActions.new || iconObj.action === iconActions.edit) {
          await orgService.updateOrgIcon(obj.id, iconObj)
        }
      }

      if (res.error) {
        const copy = cloneDeep(defaultMessages)
        copy.error = res.error.code
        setMultiMessages(copy);
      } else {
        submitModal() //{ targetId: obj.id }
        closeModal()
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(uiActions.hideModalSpinner());
    }
  }

  const closeModal = () => {
    setMultiMessages(cloneDeep(defaultMessages));
    handleModals({ modal: 'org', status: false })
  };

  useEffect(() => {
    init();
  }, [modalStatus?.org?.status]);

  return (
    <ModalContainer
      modalWidthType='modal-500px'
      openModal={modalStatus.org.status}
      closeModal={() => closeModal()}
    >
      {showModalSpinner && <SpinnerForModal />}
      <div className='header'>
        <div className='title'>{t(modalTitle?.title)}</div>
      </div>
      <div className={`body ${modalStyle['create-org-container']}`}>
        <MessageBoxGroup
          messages={multiMessages}
          ErrorMessagesType='es'
          WarningMessagesType='i18n'
          onClose={type => setMultiMessages({ ...multiMessages, [type]: null })}
        />
        <Row>
          <Col>
            <DragAndDropImgContianer
              title={modalTitle?.upload}
              accept='image/png, image/jpeg, image/jpg, image/gif, image/bmp, image/tiff, image/webp'
              onDrag={params => {
                if (isNil(params.imgSrc.action)) {
                  setModalData({ ...modalData, file: params.imgSrc.file });
                } else {
                  setModalData({ ...modalData, file: params.imgSrc.file, icon: null });
                }
              }}
              hasDefaultImg={modalData.icon}
            />
            <div className={modalStyle['upload-image-hint']}>{t('a1ae998c44')}</div>
          </Col>
          <Col>
            <div className='modal-form-title required'>{t(modalTitle?.name)}</div>
            <div className='modal-form-field'>
              <Input
                value={modalData.name.value}
                placeholder={t('7563cde047')}
                autoComplete='automatic-logout-time-limit'
                onChange={e => { changeRequired('name', e.target.value) }}
                isInvalid={modalData.name?.isInvalid}
              />
            </div>
            {modalInfo.isCreate &&
              <>
                <div className='modal-form-title required'>{t('a1fa277792')}</div>
                <div className='modal-form-field'>
                  <DropdownWithItem
                    id="license-term-dropdown"
                    type="normal"
                    selectedItem={typeOptions[0] || {}}
                    itemList={typeOptions || []}
                    disabled
                  />
                </div>
                <div className='modal-form-title required mt-2'>{t('74054af0f0')}</div>
                <div className='modal-form-field'>
                  <DropdownWithItem
                    id="org-country-dropdown"
                    type="normal"
                    selectedItem={modalData?.serviceProviderCountry || {}}
                    itemList={options?.country || []}
                    onClick={(e) => { changeCountry(e) }}
                    onKeyDown={(e) => { toTargetCountry(e) }}
                    dropDownMenuStyle={{ maxHeight: '300px', overflow: 'auto' }}
                    dropDownMenuRef={dpRef}
                  />
                </div>
              </>
            }
          </Col>
        </Row>
        <div className='form-title my-3'>{t('7b30c410c4')}</div>
        <Row>
          <Col>
            <div className='modal-form-title'>{t('49ee308734')}</div>
            <div className='modal-form-field'>
              <Input
                value={modalData.owner}
                placeholder={t('7563cde047')} // 1-64 Characters
                autoComplete='automatic-logout-time-limit'
                onChange={e => { setModalData({ ...modalData, owner: e.target.value }); }}
                isInvalid={checkInvalid('owner')}
              />
            </div>
          </Col>
          <Col>
            <div className='modal-form-title'>{t('bcc254b55c')}</div>
            <div className='modal-form-field'>
              <Input
                value={modalData.phone}
                placeholder={t('284ac11dab')}
                autoComplete='automatic-logout-time-limit'
                onChange={e => { setModalData({ ...modalData, phone: e.target.value }); }}
                isInvalid={checkInvalid('phone')}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='modal-form-title'>{t('b357b524e7')}</div>
            <div className='modal-form-field'>
              <Input
                value={modalData.email}
                placeholder={t('ca5977f4f2')}
                autoComplete='automatic-logout-time-limit'
                onChange={e => { setModalData({ ...modalData, email: e.target.value }); }}
                isInvalid={checkInvalid('email')}
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className='footer'>
        <Button
          label='4a53b84974'
          className='btn-cancel'
          onClick={() => closeModal()}
        />
        <Button
          label='c9cc8cce24'
          className='btn-submit'
          disabled={checkFormInvalid()}
          onClick={save}
        />
      </div>
    </ModalContainer>
  );
};

export default OrgManagementOrgModal;
